import type { CSSProperties } from 'react';
import { Toaster as Sonner, type ToasterProps, toast } from 'sonner';

export function SilkToaster({ position = 'bottom-right' }: { position?: ToasterProps['position'] }) {
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const theme = isDark ? 'dark' : 'light';

  // Silk UI Toast Styling - Solid backgrounds, high contrast borders
  const themedVars: CSSProperties & Record<`--${string}`, string> = {
    '--normal-bg': 'var(--background)',
    '--normal-border': 'var(--border)',
    '--normal-text': 'var(--foreground)',
    
    // Explicit high-signal semantic colors
    '--success-bg': isDark ? 'hsl(142 70% 8%)' : 'hsl(142 70% 97%)',
    '--success-border': 'var(--primary)',
    '--success-text': isDark ? 'hsl(142 70% 80%)' : 'hsl(142 70% 30%)',
    
    '--error-bg': isDark ? 'hsl(0 70% 8%)' : 'hsl(0 70% 97%)',
    '--error-border': 'var(--destructive)',
    '--error-text': isDark ? 'hsl(0 70% 80%)' : 'hsl(0 70% 30%)',
    
    '--info-bg': 'var(--muted)',
    '--info-border': 'var(--border)',
    '--info-text': 'var(--muted-foreground)',
    
    '--warning-bg': isDark ? 'hsl(30 70% 8%)' : 'hsl(30 70% 97%)',
    '--warning-border': 'hsl(30 70% 50%)',
    '--warning-text': isDark ? 'hsl(30 70% 80%)' : 'hsl(30 70% 30%)',
  };

  return (
    <Sonner
      theme={theme}
      position={position}
      style={themedVars}
      toastOptions={{
        classNames: {
          toast: 'group toast border border-border/60 bg-background text-foreground shadow-2xl rounded-xl p-4 gap-3 font-body',
          title: 'text-[13px] font-bold tracking-tight font-heading leading-none',
          description: 'text-[11px] text-muted-foreground/80 leading-relaxed font-medium',
          actionButton: 'bg-primary text-primary-foreground font-bold text-[10px] uppercase tracking-widest px-3 h-7 rounded-md transition-all active:scale-95',
          cancelButton: 'bg-muted text-muted-foreground font-bold text-[10px] uppercase tracking-widest px-3 h-7 rounded-md transition-all active:scale-95',
          closeButton: 'bg-background border border-border text-muted-foreground hover:text-foreground transition-colors',
          success: 'border-primary/40 bg-success/5',
          error: 'border-destructive/40 bg-destructive/5',
        },
      }}
      closeButton
      richColors
      expand
      duration={5000}
    />
  );
}

export { toast };
